/** @jsx jsx */
import React, { useState, useCallback, memo } from 'react';
import { jsx } from '@compiled/react';
import { useQueryLoader } from 'react-relay';
import Popup from '@atlaskit/popup';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import main_getArchivedIssuesFilterOptionsQuery, {
	type main_getArchivedIssuesFilterOptionsQuery as FilterOptionsQuery,
} from '@atlassian/jira-relay/src/__generated__/main_getArchivedIssuesFilterOptionsQuery.graphql';
import { getFilterFieldConfig } from '../../controllers/field-config.tsx';
import { useArchiveIssueActions } from '../../controllers/main.tsx';
import { useArchiveIssueStates } from '../../controllers/selectors.tsx';
import { MenuContent } from './menu-content/index.tsx';
import { Trigger } from './trigger/index.tsx';

export const Filters = memo(() => {
	const [isOpen, setIsOpen] = useState(false);
	const close = useCallback(() => {
		setIsOpen(false);
	}, []);
	const { filters, projectId } = useArchiveIssueStates();
	const { setFilters } = useArchiveIssueActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fieldConfig } = getFilterFieldConfig();
	const [queryReference, loadQuery] = useQueryLoader<FilterOptionsQuery>(
		main_getArchivedIssuesFilterOptionsQuery,
	);
	const toggle = useCallback(() => {
		projectId && loadQuery({ projectId });
		setIsOpen((prevIsOpen) => !prevIsOpen);
	}, [loadQuery, projectId]);

	const count = Object.values(filters).flat().length;

	return (
		<JSErrorBoundary
			id="issue.issue-view.activity.archived-issues-filter-selected"
			packageName="jiraArchivedIssues"
			teamName="kenji"
			fallback="unmount"
			onError={(error) => {
				fireTrackAnalytics(createAnalyticsEvent({}), 'Filters Set', {
					error,
					meta: {
						id: 'issue.issue-view.activity.archived-issues-filter-selectedr',
						teamName: 'kenji',
					},
					attributes: {
						experience: 'archivedIssuesFilterSelectionFailed',
						analyticsSource: 'jiraArchivedIssues',
						error,
					},
				});
			}}
		>
			<Popup
				isOpen={isOpen}
				onClose={close}
				placement="bottom-end"
				content={() => (
					<>
						{queryReference && (
							<MenuContent
								data-testid="issue-archival-archives.ui.filter.filter"
								count={count}
								fieldConfig={fieldConfig}
								filters={filters}
								setFilters={setFilters}
								queryReference={queryReference}
								projectId={projectId}
							/>
						)}
					</>
				)}
				trigger={(triggerProps) => (
					<Trigger
						{...triggerProps}
						count={count}
						hasFiltersApplied={count > 0}
						isSelected={isOpen}
						onClick={toggle}
					/>
				)}
			/>
		</JSErrorBoundary>
	);
});
